// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export type Codec = 'plain' | 'gzip' | 'parquet';

export interface SniffResult {
  readonly codec: Codec;
  /** Set when the file cannot be handled. Always names the reason. */
  readonly refuse?: string;
  readonly note?: string;
  /** How we decided, shown in preflight so nobody has to guess. */
  readonly how: string;
}

/**
 * Decide what a file is from its first bytes and its name.
 *
 * Magic numbers only — never the extension alone. A `.jsonl` that is actually gzipped is
 * routine, and guessing wrong means scanning compressed bytes and reporting a clean file.
 *
 * Formats we deliberately refuse BY NAME rather than mangling: zstd, bzip2, xz and zip. A
 * refusal that says which format it saw is useful; a scan of 4 GB of compressed noise that
 * reports zero findings is dangerous.
 */
export function sniff(head: Uint8Array, filename: string): SniffResult {
  const name = filename.toLowerCase();
  const b = (i: number): number => head[i] ?? -1;

  if (b(0) === 0x1f && b(1) === 0x8b) {
    return { codec: 'gzip', how: 'gzip magic bytes 1f 8b' };
  }
  if (b(0) === 0x50 && b(1) === 0x41 && b(2) === 0x52 && b(3) === 0x31) {
    return { codec: 'parquet', how: 'Parquet magic bytes PAR1' };
  }
  if (b(0) === 0x28 && b(1) === 0xb5 && b(2) === 0x2f && b(3) === 0xfd) {
    return {
      codec: 'plain',
      how: 'zstd magic bytes 28 b5 2f fd',
      refuse:
        'This is a zstd-compressed file. Scrubjay v1 does not decompress zstd — decompress it first (zstd -d) and try again.',
    };
  }
  if (b(0) === 0x42 && b(1) === 0x5a && b(2) === 0x68) {
    return { codec: 'plain', how: 'bzip2 magic bytes BZh', refuse: 'This is a bzip2 file. Decompress it first.' };
  }
  if (b(0) === 0xfd && b(1) === 0x37 && b(2) === 0x7a && b(3) === 0x58) {
    return { codec: 'plain', how: 'xz magic bytes', refuse: 'This is an xz file. Decompress it first.' };
  }
  if (b(0) === 0x50 && b(1) === 0x4b && (b(2) === 0x03 || b(2) === 0x05)) {
    return {
      codec: 'plain',
      how: 'zip magic bytes PK',
      refuse: 'This is a zip archive. Unpack it and scan the files inside.',
    };
  }
  if (name.endsWith('.parquet')) {
    return {
      codec: 'parquet',
      how: 'filename ends .parquet, though the PAR1 magic was not at byte 0',
      note: 'The Parquet magic is normally at both ends of the file; scrubjay will read the footer.',
    };
  }
  return { codec: 'plain', how: 'no compression magic — treated as text' };
}

export function guessRecordShape(firstLine: string): 'ndjson' | 'json-array' | 'csv' | 'text' {
  const t = firstLine.trimStart();
  if (t.startsWith('{')) return 'ndjson';
  if (t.startsWith('[')) {
    // JSON Lines permits ANY JSON value per line, and token-id rows like `[101, 2054, 102]`
    // plus message-array rows are the two most common shapes in exactly this domain. So a
    // leading `[` only means a pretty-printed top-level array if the line does not parse.
    try {
      JSON.parse(firstLine);
      return 'ndjson';
    } catch {
      return 'json-array';
    }
  }
  if (firstLine.includes(',') && !firstLine.includes('\t')) return 'csv';
  return 'text';
}
